import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Bookmark, NotebookPen, ChevronUp, ChevronDown, ArrowUpToLine, AlertTriangle, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MaskedLines, FadeUp } from "../components/Reveal";
import CoverImage from "../components/CoverImage";
import ReadingStats from "../components/ReadingStats";
import EntryActions from "../components/EntryActions";
import NoteDialog from "../components/NoteDialog";
import { toast } from "sonner";

const TYPE_LABEL = { book: "Book", film: "Film", tv: "Series", youtube: "Video", essay: "Essay" };

export default function MyLibrary() {
  const { user, login } = useAuth();
  const [shortlist, setShortlist] = useState([]);
  const [notes, setNotes] = useState([]);
  const [noteItem, setNoteItem] = useState(null);
  const [progress, setProgress] = useState({});
  const [curriculums, setCurriculums] = useState([]);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [wiping, setWiping] = useState(false);

  const load = useCallback(() => {
    if (!user) return;
    Promise.all([api.get("/shortlist"), api.get("/notes"), api.get("/progress"), api.get("/curriculums")])
      .then(([s, n, p, c]) => {
        setShortlist(s.data.items);
        setNotes(n.data);
        setProgress(p.data);
        setCurriculums(c.data);
      })
      .catch((e) => console.error("Could not load your account", e));
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const persistOrder = (list) => {
    setShortlist(list);
    api.post("/shortlist/reorder", { item_ids: list.map((i) => i.id) })
      .catch((e) => console.error("Could not save your TBR order", e));
  };

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= shortlist.length) return;
    const list = [...shortlist];
    [list[index], list[target]] = [list[target], list[index]];
    persistOrder(list);
  };

  const moveToTop = (index) => {
    if (index === 0) return;
    const list = [...shortlist];
    persistOrder([list[index], ...list.filter((_, n) => n !== index)]);
  };

  const deleteAccount = async () => {
    setWiping(true);
    try {
      await api.delete("/account");
      toast.success("Your account and data have been deleted");
      window.location.replace("/");
    } catch (e) {
      console.error("Account deletion failed", e);
      toast.error("Could not delete your account — please try again");
      setWiping(false);
    }
  };

  const inProgress = curriculums.map((c) => {
    const total = (c.modules || []).reduce((a, m) => a + (m.steps?.length || 0), 0);
    const done = (progress[c.id] || []).length;
    return { c, total, done, pct: total ? Math.round((done / total) * 100) : 0 };
  }).filter((x) => x.done > 0 && x.done < x.total);

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 pt-20" data-testid="mylibrary-guest">
        <p className="overline text-crimson mb-6">My Account</p>
        <h1 className="font-serif text-5xl lg:text-7xl font-light text-vellum text-center leading-[0.9]">Your library awaits.</h1>
        <p className="mt-6 max-w-md text-center text-vellum-muted leading-relaxed">Sign in to track your progress, create a TBR list, and add notes.</p>
        <button onClick={login} data-testid="mylibrary-signin" className="mt-10 font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 border border-crimson text-crimson hover:bg-crimson hover:text-onaccent transition-colors">Sign in with Google</button>
      </div>
    );
  }

  return (
    <div data-testid="mylibrary-page" className="pt-16 lg:pt-20">
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-20 lg:pt-28 pb-16">
        <FadeUp><p className="overline mb-8 text-crimson">Your private space</p></FadeUp>
        <h1 className="font-serif font-light tracking-tighter leading-[0.88] text-vellum text-[clamp(2.5rem,8vw,6.5rem)]" data-testid="account-greeting">
          <MaskedLines lines={["Welcome back,"]} />
          <MaskedLines lines={[user.name || user.email]} delay={0.15} lineClassName="italic text-brass" />
        </h1>
        <FadeUp delay={0.2}>
          <div className="mt-8 flex gap-8">
            <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-vellum-muted"><Bookmark className="w-4 h-4 text-crimson" /> {shortlist.length} on your TBR</span>
            <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-vellum-muted"><NotebookPen className="w-4 h-4 text-brass" /> {notes.length} notes</span>
          </div>
        </FadeUp>
      </section>

      {/* Your progress */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 pb-4" data-testid="account-progress">
        <ReadingStats />
      </section>

      {/* Continue where you left off */}
      {inProgress.length > 0 && (
        <section className="bg-ink-800 py-20 lg:py-28 mt-12" data-testid="continue-reading">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
            <FadeUp>
              <h2 className="font-serif text-4xl lg:text-6xl font-light text-vellum tracking-tight">
                Continue where<br /><span className="italic text-brass">you left off</span>
              </h2>
            </FadeUp>
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line">
              {inProgress.slice(0, 3).map(({ c, pct, done, total }) => (
                <Link key={c.id} to={`/curriculums/${c.id}`} data-testid={`continue-${c.id}`} className="group bg-ink p-8 hover:bg-ink-700 transition-colors">
                  <p className="overline text-brass mb-3">{c.field}</p>
                  <h3 className="font-serif text-2xl lg:text-3xl font-light text-vellum group-hover:text-brass transition-colors leading-tight">{c.title}</h3>
                  <div className="mt-6 flex items-center gap-3">
                    <div className="flex-1 h-px bg-line relative"><div className="absolute left-0 top-0 h-px bg-crimson" style={{ width: `${pct}%` }} /></div>
                    <span className="font-mono text-xs text-brass">{done}/{total}</span>
                  </div>
                  <span className="mt-4 inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.2em] text-vellum-muted group-hover:text-crimson transition-colors">Resume <ArrowUpRight className="w-3.5 h-3.5" /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TBR */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 pb-20 pt-16">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-3">
          <h2 className="font-serif text-3xl lg:text-4xl font-light text-vellum">Your TBR</h2>
          {shortlist.length > 1 && (
            <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-vellum-faint">Reorder so your next read sits first</p>
          )}
        </div>
        {shortlist.length === 0 ? (
          <p className="text-vellum-muted">Nothing on your TBR yet. Tap the bookmark on any entry in <Link to="/library" className="text-brass link-underline">The Library</Link>.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-14">
            {shortlist.map((item, index) => (
              <article key={item.id} className="group" data-testid={`saved-item-${item.id}`}>
                <Link to={`/library/${item.id}`} className="block">
                  <div className={`relative overflow-hidden border border-line mb-4 ${item.media_type === "book" || item.media_type === "essay" ? "aspect-[2/3]" : "aspect-video"}`}>
                    <CoverImage src={item.cover_url} alt={item.title} focal={item.cover_focal} title={item.title} creator={item.creator} year={item.year} />
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-[10px] text-brass" data-testid={`tbr-position-${item.id}`}>{String(index + 1).padStart(2, "0")}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-crimson">{TYPE_LABEL[item.media_type]}</span>
                  </div>
                  <h3 className="font-serif text-xl text-vellum group-hover:text-brass transition-colors leading-tight">{item.title}</h3>
                  <p className="font-mono text-xs text-vellum-faint mt-1">{item.creator}</p>
                </Link>
                <div className="mt-3 flex items-center justify-between gap-3">
                  <EntryActions item={item} onOpenNote={setNoteItem} onChanged={load} />
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => moveToTop(index)}
                      disabled={index === 0}
                      data-testid={`tbr-top-${item.id}`}
                      aria-label={`Move ${item.title} to the top of your TBR`}
                      title="Read this next"
                      className="p-1.5 text-vellum-faint hover:text-brass transition-colors disabled:opacity-20 disabled:hover:text-vellum-faint"
                    >
                      <ArrowUpToLine className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => move(index, -1)}
                      disabled={index === 0}
                      data-testid={`tbr-up-${item.id}`}
                      aria-label={`Move ${item.title} up`}
                      className="p-1.5 text-vellum-faint hover:text-onaccent transition-colors disabled:opacity-20 disabled:hover:text-vellum-faint"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => move(index, 1)}
                      disabled={index === shortlist.length - 1}
                      data-testid={`tbr-down-${item.id}`}
                      aria-label={`Move ${item.title} down`}
                      className="p-1.5 text-vellum-faint hover:text-onaccent transition-colors disabled:opacity-20 disabled:hover:text-vellum-faint"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Notes */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 pb-28 pt-16">
        <h2 className="font-serif text-3xl lg:text-4xl font-light text-vellum mb-10">Notes</h2>
        {notes.length === 0 ? (
          <p className="text-vellum-muted">No notes yet. Tap the pen icon on any entry to jot a private thought.</p>
        ) : (
          <div className="divide-y divide-line border-t border-line">
            {notes.map((n) => (
              <div key={n.item_id} data-testid={`note-item-${n.item_id}`} className="py-8 grid lg:grid-cols-12 gap-6 items-start">
                <div className="lg:col-span-4">
                  {n.item && <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-crimson">{TYPE_LABEL[n.item.media_type]}</span>}
                  <h3 className="font-serif text-2xl text-vellum leading-tight">{n.item ? n.item.title : "Untitled"}</h3>
                  {n.item && <p className="font-mono text-xs text-vellum-faint mt-1">{n.item.creator}</p>}
                </div>
                <p className="lg:col-span-6 text-vellum-muted italic leading-relaxed">"{n.text}"</p>
                <div className="lg:col-span-2 flex lg:justify-end gap-4">
                  {n.item && <EntryActions item={n.item} onOpenNote={setNoteItem} onChanged={load} />}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Danger zone */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 pb-28" data-testid="account-danger">
        <div className="border border-line p-8 lg:p-10">
          <h2 className="font-serif text-2xl lg:text-3xl font-light text-vellum">Delete your account</h2>
          <p className="mt-3 max-w-2xl text-sm text-vellum-muted leading-relaxed">
            Removes your account and everything attached to it — your TBR list, your notes and your
            curriculum progress. The library itself stays as it is. This cannot be undone.
          </p>
          <button
            onClick={() => { setTyped(""); setWipeOpen(true); }}
            data-testid="account-delete-open"
            className="mt-7 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 border border-crimson/60 text-crimson hover:bg-crimson hover:text-onaccent transition-colors"
          >
            <Trash2 className="w-4 h-4" /> Delete my account and data
          </button>
        </div>
      </section>

      {wipeOpen && (
        <div className="fixed inset-0 z-[80] bg-ink/90 backdrop-blur-sm flex items-center justify-center px-6" data-lenis-prevent onClick={() => setWipeOpen(false)}>
          <div className="w-full max-w-lg bg-ink-800 border border-crimson/50 p-8" onClick={(e) => e.stopPropagation()} data-testid="account-delete-dialog">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-crimson shrink-0 mt-1" />
              <div>
                <h3 className="font-serif text-2xl text-vellum leading-tight">Delete your account?</h3>
                <p className="mt-3 text-sm text-vellum-muted leading-relaxed">
                  {shortlist.length} TBR {shortlist.length === 1 ? "entry" : "entries"}, {notes.length} {notes.length === 1 ? "note" : "notes"} and
                  all curriculum progress will be permanently erased. You can sign in again later, but you will start fresh.
                </p>
              </div>
            </div>
            <label className="block mt-8">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-vellum-faint">Type DELETE to confirm</span>
              <input
                autoFocus
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                data-testid="account-delete-input"
                className="mt-2 w-full bg-ink border border-line px-4 py-3 font-mono text-sm tracking-[0.2em] text-vellum focus:border-crimson outline-none"
              />
            </label>
            <div className="mt-8 flex items-center justify-end gap-4">
              <button onClick={() => setWipeOpen(false)} data-testid="account-delete-cancel" className="font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 border border-line text-vellum-muted hover:text-vellum transition-colors">Cancel</button>
              <button
                onClick={deleteAccount}
                disabled={typed.trim().toUpperCase() !== "DELETE" || wiping}
                data-testid="account-delete-confirm"
                className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 bg-crimson text-onaccent hover:bg-crimson-hover transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-4 h-4" /> {wiping ? "Deleting…" : "Delete everything"}
              </button>
            </div>
          </div>
        </div>
      )}

      {noteItem && <NoteDialog item={noteItem} onClose={() => setNoteItem(null)} onSaved={load} />}
    </div>
  );
}
